import type { AttemptInput } from '@dcs/contracts';
import type { KeyValueStorage } from '@dcs/platform/ports';
import { createWebStorage } from '@dcs/platform/web';
import {
  createContext,
  use,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useAuth, usePorts, type AuthStatus } from '../../../shared/providers';
import { createAttemptQueue, type AttemptQueue, type AttemptStatus } from '../queue';
import { OWNER_HINT_KEY, emitAttemptSaved, registerAttemptQueue } from './save-registry';
import { createApiGateway, type SessionProbe } from './sender';

/**
 * What a result screen needs to save a finished exam (F6 T-2.2), provided once at the app root:
 *
 * - `checking`: not decided yet (the session is loading, or being verified).
 * - `guest`: not signed in. Nothing is queued and nothing is sent; the screen shows an invitation.
 * - `account`: signed in, or signed in a moment ago and the session cannot be verified right now
 *   (see `OWNER_HINT_KEY`). The result goes into the offline queue under `owner`.
 *
 * A result screen reads this once, when its exam finishes, and keeps that decision (see
 * `useAttemptSave`): a guest's result is never uploaded, whatever the session does afterwards.
 */
export type SaveAccess = 'checking' | 'guest' | 'account';

export interface AttemptSaver {
  readonly access: SaveAccess;
  /** Who owns a result finished now; `null` unless `access` is `account`. */
  readonly owner: string | null;
  /** Queues `input` under `owner` and sends it if that user is signed in and online. */
  save(input: AttemptInput, owner: string): void;
  /** `true` when queued results of `owner` are being sent now (signed in as them, and online). */
  sendingFor(owner: string): boolean;
  statusOf(key: string): AttemptStatus;
  subscribe(listener: () => void): () => void;
  version(): number;
}

const SaverContext = createContext<AttemptSaver | null>(null);

/** The saver, or `null` outside the provider (tests of a result screen alone, the site). */
export function useAttemptSaver(): AttemptSaver | null {
  return use(SaverContext);
}

/** How often a signed-in tab looks for queued results that are due (backoff is per item). */
export const FLUSH_INTERVAL_MS = 30_000;

/** Longest wait between two session checks while the server "cannot tell" (T-2.2 m-a). */
export const PROBE_BACKOFF_MAX_MS = 15 * 60_000;

/** Delay before the next session check after `failures` failed ones: base, ×2 each, capped. */
export function probeRetryDelay(baseMs: number, failures: number): number {
  const steps = Math.max(0, failures - 1);
  return Math.min(PROBE_BACKOFF_MAX_MS, baseMs * 2 ** Math.min(steps, 20));
}

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}
const isOnline = (): boolean => navigator.onLine;

/** What the server said when asked about a session the store reads as signed out. */
type Probe =
  | { readonly kind: 'idle' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'error' }
  | { readonly kind: 'user'; readonly id: string };

export interface AttemptSaveCoreProps {
  readonly authStatus: AuthStatus;
  readonly userId: string | null;
  readonly queue: AttemptQueue;
  readonly storage: KeyValueStorage;
  /** Fresh "who is signed in?" (a real `/api/me`), used to tell signed out from "cannot tell". */
  readonly session: SessionProbe;
  readonly intervalMs?: number;
  readonly onError?: (error: unknown) => void;
  readonly children: ReactNode;
}

/**
 * The provider without its wiring (session, storage, queue are props), which is what tests mount.
 *
 * Who owns a new result:
 * - signed in: that user. The id is remembered in storage (`OWNER_HINT_KEY`).
 * - the store reads signed out, but the device remembers a user: Better Auth also says "signed out"
 *   when `get-session` merely fails (offline, lie-fi, a 5xx), so the server is asked directly. Only an
 *   explicit "anonymous" answer makes it a guest and clears the hint; "cannot tell" (error, timeout,
 *   offline) keeps that user as the owner and the results queued, to be sent when it can be verified.
 * - no remembered user: a guest, with no request made.
 *
 * Purging (T-2.2 M-a, design.md "Session hết hạn khi đang thi"): only an explicit sign-out
 * (`AuthProvider.signOut`) and the account deletion (`purgeAttemptQueue`) remove a user's queue, dead
 * letters and hint. A session that merely ended (expired, revoked elsewhere), whether the app is open
 * or not, looks like "signed out" here: once the server confirms it, only the hint goes (new results
 * are a guest's), and the queued results stay (for up to 7 days) for that user's next sign-in.
 */
export function AttemptSaveCore({
  authStatus,
  userId,
  queue,
  storage,
  session,
  intervalMs = FLUSH_INTERVAL_MS,
  onError,
  children,
}: AttemptSaveCoreProps) {
  const online = useSyncExternalStore(subscribeOnline, isOnline, () => true);
  const signedInId = authStatus === 'authenticated' ? userId : null;
  const [probe, setProbe] = useState<Probe>({ kind: 'idle' });
  const [reprobe, setReprobe] = useState(0);
  /** Consecutive "cannot tell" answers of the server (not counting offline): the backoff step. */
  const [failedProbes, setFailedProbes] = useState(0);

  useEffect(() => {
    registerAttemptQueue({ queue, storage });
    const stop = queue.subscribeSaved(emitAttemptSaved);
    return () => {
      stop();
      registerAttemptQueue(null);
    };
  }, [queue, storage]);

  useEffect(() => {
    if (signedInId === null) return;
    storage.set(OWNER_HINT_KEY, signedInId);
    setProbe({ kind: 'idle' });
  }, [signedInId, storage]);

  // The store reads signed out: ask the server, if a user is remembered.
  const remembered = authStatus === 'anonymous' ? storage.get(OWNER_HINT_KEY) : null;
  const probing = authStatus === 'anonymous' && remembered !== null;
  useEffect(() => {
    if (!probing) return undefined;
    if (!online) {
      setProbe({ kind: 'error' });
      return undefined;
    }
    let live = true;
    setProbe((previous) => (previous.kind === 'error' ? previous : { kind: 'checking' }));
    session().then(
      (check) => {
        if (!live) return;
        if (check.kind === 'anonymous') {
          // Signed out without the explicit sign-out (that one purged already): keep the queue.
          storage.remove(OWNER_HINT_KEY);
          setFailedProbes(0);
          setProbe({ kind: 'anonymous' });
        } else if (check.kind === 'user') {
          setFailedProbes(0);
          setProbe({ kind: 'user', id: check.id });
        } else {
          setFailedProbes((n) => n + 1);
          setProbe({ kind: 'error' });
        }
      },
      () => {
        if (!live) return;
        setFailedProbes((n) => n + 1);
        setProbe({ kind: 'error' });
      },
    );
    return () => {
      live = false;
    };
  }, [probing, online, reprobe, session, storage]);

  // "Cannot tell" is retried: at once on reconnect (`online` above), else after a growing delay
  // (T-2.2 m-a): `intervalMs`, then doubled after each failed answer, at most 15 minutes, so a
  // device stuck behind a broken network does not call `/api/me` every 30 s for hours.
  const unverified = probing && probe.kind === 'error' && online;
  useEffect(() => {
    if (!unverified) return undefined;
    const timer = setTimeout(
      () => {
        setReprobe((n) => n + 1);
      },
      probeRetryDelay(intervalMs, failedProbes),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [unverified, intervalMs, failedProbes]);

  let access: SaveAccess;
  let owner: string | null = null;
  if (authStatus === 'loading') access = 'checking';
  else if (signedInId !== null) {
    access = 'account';
    owner = signedInId;
  } else if (remembered === null) access = 'guest';
  else if (probe.kind === 'anonymous') access = 'guest';
  else if (probe.kind === 'error') {
    access = 'account';
    owner = remembered;
  } else if (probe.kind === 'user') {
    access = 'account';
    owner = probe.id;
  } else access = 'checking';

  const sending = signedInId !== null && online;

  // Start, sign-in and "back online" all flip `sending`; the interval covers items waiting on backoff.
  useEffect(() => {
    if (signedInId === null || !online) return undefined;
    const run = (): void => {
      queue.flush(signedInId).catch((error: unknown) => onError?.(error));
    };
    run();
    const timer = setInterval(run, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [signedInId, online, queue, intervalMs, onError]);

  const saver = useMemo<AttemptSaver>(
    () => ({
      access,
      owner,
      save(input, forOwner) {
        if (!queue.enqueue(input, forOwner)) return;
        if (!sending || forOwner !== signedInId) return;
        queue.flush(forOwner).catch((error: unknown) => onError?.(error));
      },
      sendingFor: (forOwner) => sending && forOwner === signedInId,
      statusOf: (key) => queue.statusOf(key),
      subscribe: (listener) => queue.subscribe(listener),
      version: () => queue.version(),
    }),
    [access, owner, sending, signedInId, queue, onError],
  );

  return <SaverContext value={saver}>{children}</SaverContext>;
}

/** The app-root provider: session from `AuthProvider`, storage from the platform adapter. */
export function AttemptSaveProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { errorReporter } = usePorts();
  const [wiring] = useState(() => {
    const storage = createWebStorage();
    const gateway = createApiGateway();
    const queue = createAttemptQueue({
      storage,
      send: gateway.send,
      session: gateway.session,
      report: (error) => {
        errorReporter.report(error, { source: 'manual' });
      },
    });
    return { storage, queue, session: gateway.session };
  });
  const onError = useMemo(
    () => (error: unknown) => {
      errorReporter.report(error, { source: 'manual' });
    },
    [errorReporter],
  );

  return (
    <AttemptSaveCore
      authStatus={auth.status}
      userId={auth.user?.id ?? null}
      queue={wiring.queue}
      storage={wiring.storage}
      session={wiring.session}
      onError={onError}
    >
      {children}
    </AttemptSaveCore>
  );
}
